import { useMemo, useState } from "react";
import { Loader2, MapPin, Lasso } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import {
  EMPTY_UI_FILTERS,
  filterRows,
  useMegamap,
  type MegamapProject,
  type UiFilters,
} from "../megamap/data";
import { aggregate, proximityZones, zoomToThresholdKm, type Zone } from "../megamap/clustering";
import type { ColorDim, SizeDim } from "../megamap/colors";
import { MegamapCanvas } from "../megamap/MegamapCanvas";
import { CoordinatorLoadTable } from "../megamap/CoordinatorLoadTable";
import {
  buildOptions,
  ColorByPanel,
  FiltersPanel,
  MapLayersPanel,
  RollupPanel,
  SelectionPanel,
  ZonesPanel,
} from "../megamap/panels";

export function ZEMegaMapPage() {
  const user = useAuthStore((s) => s.user);
  // Technical director and above only (mirrors the backend IsTechnicalDirector gate).
  const canAccess = !!user && (user.is_ceo || (user.role === "director" && user.is_technical_director));

  const { data, isLoading, isError } = useMegamap({}, canAccess);
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const [filters, setFilters] = useState<UiFilters>(EMPTY_UI_FILTERS);
  const [colorDim, setColorDim] = useState<ColorDim>("needs");
  const [sizeDim, setSizeDim] = useState<SizeDim>("value");
  const [lasso, setLasso] = useState(false);
  const [voivodeshipLines, setVoivodeshipLines] = useState(true);
  const [viewport, setViewport] = useState<MegamapProject[]>([]);
  const [selection, setSelection] = useState<MegamapProject[]>([]);
  const [zoom, setZoom] = useState(6);
  const [focus, setFocus] = useState<{ lat: number; lng: number; key: number } | null>(null);

  // Proximity radius tracks the map zoom: regional zones when zoomed out, local when zoomed in.
  const thresholdKm = useMemo(() => zoomToThresholdKm(zoom), [zoom]);
  const filtered = useMemo(() => filterRows(rows, filters), [rows, filters]);
  const options = useMemo(() => buildOptions(rows), [rows]);
  const zones = useMemo(() => proximityZones(filtered, thresholdKm), [filtered, thresholdKm]);
  const viewportAgg = useMemo(() => aggregate(viewport), [viewport]);
  const selectionAgg = useMemo(() => aggregate(selection), [selection]);
  const unlocated = filtered.filter((p) => !p.located).length;

  const focusZone = (z: Zone) => setFocus({ lat: z.lat, lng: z.lng, key: Date.now() });

  if (!canAccess) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        Ta sekcja jest dostępna tylko dla dyrektora technicznego i zarządu.
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-gray-400">
        <Loader2 className="mr-2 animate-spin" size={18} /> Ładowanie megamapy…
      </div>
    );
  }
  if (isError) {
    return <div className="flex h-full items-center justify-center text-red-500">Nie udało się wczytać danych megamapy.</div>;
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto">
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-2 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <MapPin size={18} className="text-red-600" />
          <h1 className="text-base font-semibold">ZE Megamapa</h1>
          <span className="text-xs text-gray-500">{filtered.length} projektów{unlocated > 0 ? ` · ${unlocated} bez lokalizacji` : ""}</span>
        </div>
        <button
          type="button"
          onClick={() => { setLasso((v) => !v); if (lasso) setSelection([]); }}
          className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
            lasso ? "bg-red-600 text-white" : "border border-gray-300 text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          }`}
          title="Narysuj region, aby zaznaczyć projekty i zobaczyć ich sumy"
        >
          <Lasso size={14} /> {lasso ? "Rysowanie… (kliknij, by zamknąć)" : "Zaznacz region"}
        </button>
      </div>

      {/* The map keeps most of the viewport; the roll-up table lives below it and the page scrolls. */}
      <div className="flex h-[72vh] min-h-[440px] shrink-0">
        <div className="min-w-0 flex-1">
          <MegamapCanvas
            rows={filtered}
            colorDim={colorDim}
            sizeDim={sizeDim}
            lasso={lasso}
            focus={focus}
            onViewportChange={setViewport}
            onLassoSelect={setSelection}
            onZoomChange={setZoom}
            voivodeshipLines={voivodeshipLines}
          />
        </div>
        <div className="w-80 shrink-0 space-y-3 overflow-y-auto border-l border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
          <FiltersPanel f={filters} onChange={setFilters} options={options} total={rows.length} shown={filtered.length} />
          <RollupPanel title="Widoczny obszar" agg={viewportAgg} />
          <SelectionPanel items={selection} agg={selectionAgg} onClear={() => setSelection([])} />
          <ColorByPanel dim={colorDim} onDim={setColorDim} sizeDim={sizeDim} onSizeDim={setSizeDim} rows={filtered} />
          <MapLayersPanel voivodeshipLines={voivodeshipLines} onVoivodeshipLines={setVoivodeshipLines} />
          <ZonesPanel zones={zones} onZoom={focusZone} thresholdKm={thresholdKm} />
        </div>
      </div>

      <div className="shrink-0 border-t border-gray-200 p-4 dark:border-gray-700">
        <CoordinatorLoadTable rows={filtered} />
      </div>
    </div>
  );
}

export default ZEMegaMapPage;
